import React, { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "../supabase";
import { useAuth } from "../context/AuthContext";
import {
  Save,
  Building2,
  MapPin,
  Mail,
  Phone,
  Search,
  Monitor,
  RefreshCcw,
  Info,
  Hash,
  Settings2,
  ToggleRight,
  ToggleLeft,
  Layers,
  Check,
  DollarSign,
  Package,
  Calculator,
  TrendingUp,
  Award,
  Database, // ✅ IMPORTADO EL ÍCONO NUEVO
  Image as ImageIcon, // ✅ IMPORTADO PARA EL TOGGLE DE FOTOS
  Store, // ✅ Para el toggle de lista de precios por sitio
  Power, // ✅ Para el toggle de baja de clientes
  FolderKanban, // ✅ Para el toggle del tablero Kanban del CRM
  Trash2, // ✅ Para los toggles de eliminación de productos
  Hammer, // ✅ Para el toggle de produccion sin stock
  Lock, // ✅ Para marcar los toggles que solo habilita soporte
  Wallet, // ✅ Para el grupo de reglas de caja
  Users, // ✅ Para el grupo de reglas de clientes / CRM
  Scale, // ✅ Para el toggle de punto de equilibrio
  Bell, // ✅ Para el aviso push de stock crítico
  Percent, // Descuento por producto
  CalendarClock, // Vencimientos y lotes
} from "lucide-react";
import toast from "react-hot-toast";
import {
  DEFAULT_POS_UI_CONFIG,
  getPosUiConfigFromPrintConfig,
  mergePosUiConfigIntoPrintConfig,
  POS_ZOOM_OPTIONS,
} from "../utils/posUiConfig";
import {
  DEFAULT_SYSTEM_UI_CONFIG,
  getSystemUiConfigFromPrintConfig,
  mergeSystemUiConfigIntoPrintConfig,
  SYSTEM_ZOOM_OPTIONS,
  broadcastSystemUiConfigChange,
} from "../utils/systemUiConfig";
import {
  DEFAULT_RECENT_MOVEMENTS_LIMIT,
  MAX_RECENT_MOVEMENTS_LIMIT,
  resolveRecentMovementsLimit,
} from "../utils/cashCloseVisibility";
import { formatLocalTimestampWithOffset } from "../utils/localDateTime";

/**
 * Reglas operativas, agrupadas por DONDE aplica cada logico.
 *
 * Antes era una sola grilla de ~19 toggles sin orden: el dueno no tenia forma de
 * saber si "Merma esperada" tocaba el POS o la produccion, y los dos logicos de
 * eliminacion de productos quedaban lejos de los de stock. Ahora cada grupo dice
 * a que pantalla afecta y cada regla lleva su explicacion (`hint`), que se
 * muestra en el tooltip y se puede abrir dentro de la tarjeta (en celular el
 * tooltip nativo no existe).
 *
 * `requiere` / `requiereLabel`: el logico queda bloqueado hasta prender el padre.
 * El padre puede vivir en el mismo grupo o en otro; se resuelve contra formData.
 *
 * NOTA: `auto_round` (Redondeo automatico) NO figura mas aca. La columna existe
 * y se conserva, pero ninguna pantalla del sistema la lee: era un toggle que no
 * hacia nada. Si se implementa el redondeo, volver a agregarlo a "Punto de venta".
 */
const REGLAS_OPERATIVAS = [
  {
    id: "pos",
    label: "Punto de venta",
    description: "Cómo se comporta la pantalla de ventas",
    icon: <Store size={14} />,
    rules: [
      {
        id: "enable_misc_product",
        label: "Habilitar Productos Varios",
        hint: "Agrega en el POS un ítem de precio libre para cobrar algo que no está cargado en el catálogo. No descuenta stock ni lleva costo.",
        icon: <Calculator size={14} />,
      },
      {
        id: "enable_dynamic_combos",
        label: "Combos Dinámicos (POS)",
        hint: "Permite combos por pasos: el cliente elige los productos de cada paso al momento de la venta (por ejemplo hamburguesa + guarnición + bebida). Los combos se arman en el constructor de combos.",
        icon: <Layers size={14} />,
      },
      {
        // Descuento por producto (por línea del carrito). Ver utils/descuentoItem.js.
        id: "enable_item_discount",
        label: "Descuento por producto",
        hint: "Permite aplicar un descuento a un producto puntual del carrito (en % o con precio final), además del descuento general de la venta. Los productos marcados \"sin descuentos\" no lo admiten.",
        icon: <Percent size={14} />,
      },
      {
        // Lógico numérico: prendido = tope en %, apagado = null (sin tope).
        id: "item_discount_max_pct",
        type: "number",
        defaultValue: 20,
        unit: "%",
        inputLabel: "Tope",
        inputSuffix: "como máximo",
        min: 1,
        max: 100,
        label: "Tope de descuento por producto",
        hint: "Máximo % que puede aplicar un usuario sin permiso de autorizar descuentos. Por encima, el POS pide que lo autorice un usuario habilitado (admin o con el permiso \"POS: Autorizar descuentos\"). Apagado: sin tope.",
        icon: <Percent size={14} />,
        requiere: "enable_item_discount",
        requiereLabel: "Descuento por producto",
      },
      {
        // Lógico numérico: prendido = umbral en %, apagado = null (motivo opcional).
        id: "item_discount_reason_from_pct",
        type: "number",
        defaultValue: 15,
        unit: "%",
        inputLabel: "Pedir motivo desde",
        inputSuffix: "o más",
        min: 1,
        max: 100,
        label: "Motivo obligatorio desde",
        hint: "A partir de este %, el POS pide un motivo para el descuento por producto (queda guardado en la venta). Apagado: el motivo es opcional.",
        icon: <Percent size={14} />,
        requiere: "enable_item_discount",
        requiereLabel: "Descuento por producto",
      },
      {
        id: "use_pos_price_list_per_site",
        label: "Lista de precios por sitio (POS)",
        hint: "Cada sucursal vende con la lista de precios asignada en su ficha. Sin esto, todas usan el precio general del producto. Si la lista asignada está desactivada, se ignora y vuelve al precio general.",
        icon: <Store size={14} />,
      },
    ],
  },
  {
    id: "caja",
    label: "Caja y cobros",
    description: "Turnos, arqueo y cuándo impacta la plata",
    icon: <Wallet size={14} />,
    rules: [
      {
        id: "blind_cash_close",
        label: "Cierre ciego",
        hint: "El cajero declara lo que contó sin ver el saldo teórico ni la diferencia. Es el control para detectar faltantes: si ve el teórico, puede declarar justo ese número. Los permisos finos de Caja (ver movimientos, imprimir arqueo, anular) solo restringen con este lógico activo.",
        icon: <Hash size={14} />,
      },
      {
        id: "alert_arqueo_cero",
        label: "Alertar arqueo en cero",
        hint: "Avisa al cerrar el turno cuando el arqueo quedó en $0, para el que se olvidó de contar. Apagado (por defecto) no molesta a las empresas que cierran en cero a propósito.",
        icon: <Calculator size={14} />,
      },
      {
        id: "enable_arqueo_review",
        label: "Control de arqueos",
        hint: "Habilita en Caja una pestaña donde el dueño marca cada arqueo de cierre como controlado, con su firma y la fecha. Solo entran los arqueos cerrados a partir del momento en que se activa, y solo un administrador puede marcarlos.",
        icon: <Check size={14} />,
      },
      {
        id: "defer_non_cf_sales_collection",
        label: "Diferir cobro no CF",
        hint: "Las ventas a clientes que no son consumidor final no impactan la caja al emitirse: quedan en cuenta corriente y se cobran después. Los consumidores finales se siguen cobrando en el momento.",
        icon: <DollarSign size={14} />,
      },
    ],
  },
  {
    id: "productos",
    label: "Productos y stock",
    description: "Catálogo, precios y control de existencias",
    icon: <Package size={14} />,
    rules: [
      {
        id: "enable_variants",
        label: "Variantes",
        hint: "Opciones de texto libre (sabor, tamaño, presentación) que se eligen al vender. Todas comparten el stock del producto.",
        icon: <Layers size={14} />,
      },
      {
        id: "enable_variant_stock",
        label: "Talles y colores",
        hint: "Para indumentaria y calzado: cada producto puede tener su matriz talle × color con stock propio por talle en cada sucursal (POS, tienda online, ajustes, transferencias y cambios de talle). Apagado, todo funciona como siempre. Los productos que ya pasaste a talles siguen manejándose por talle aunque lo apagues.",
        icon: <Layers size={14} />,
      },
      {
        id: "allow_zero_sell_price",
        label: "Permitir Precio Venta $0",
        hint: "Deja guardar productos con precio de venta en cero. Sirve para insumos, promos o ítems que se cargan sin precio; sin esto el sistema exige un precio mayor a cero.",
        icon: <DollarSign size={14} />,
      },
      {
        id: "allow_negative_stock",
        label: "Permitir Stock Negativo",
        hint: "El POS deja confirmar la venta aunque no haya stock suficiente. Como el saldo puede quedar en negativo, la tarjeta de producto deja de mostrar el stock salvo que actives el lógico siguiente.",
        icon: <Package size={14} />,
      },
      {
        id: "show_pos_negative_stock",
        label: "Mostrar stock negativo en el POS",
        hint: "Con stock negativo permitido, el POS igual muestra el stock en cada tarjeta y lo pinta en rojo cuando está en cero o negativo. Sirve para ver de un vistazo qué productos hay que reponer o cargar.",
        icon: <Package size={14} />,
        requiere: "allow_negative_stock",
        requiereLabel: "Permitir Stock Negativo",
      },
      {
        // Lógico numérico: prendido = hay umbral (organizations.critical_stock_threshold).
        // Apagado = null. Lo evalúa el trigger push_on_stock_change.
        id: "critical_stock_threshold",
        type: "number",
        defaultValue: 3,
        unit: "u.",
        label: "Aviso push de stock crítico",
        hint: "Manda una notificación push a los dispositivos con avisos activados (Tienda → Notificaciones) cuando un producto visible en la tienda baja hasta este número de unidades en la sucursal que abastece la tienda. Avisa una vez al cruzar el umbral, no en cada venta; si varios productos cruzan juntos, llega un solo resumen. Un producto que ya estaba por debajo al activarlo avisa recién cuando se reponga y vuelva a bajar. No reemplaza al stock mínimo de cada producto.",
        icon: <Bell size={14} />,
      },
      {
        // Vencimientos y lotes. Ver utils/stockLotes.js y la migración
        // 20261028200000_vencimientos_lotes.sql (el trigger lleva los lotes).
        id: "enable_expiry_tracking",
        label: "Vencimientos y lotes",
        hint: "Al recibir mercadería se carga la fecha de vencimiento (y el lote si el proveedor lo informa) de los productos marcados \"Controla vencimiento\". Las ventas descuentan primero lo que vence antes, sin que el cajero elija lote. Habilita Inventario → Vencimientos. Al activarlo, el stock que ya tenés queda como \"sin lote\" hasta que lo reclasifiques. No aplica a productos con talles y colores.",
        icon: <CalendarClock size={14} />,
      },
      {
        id: "block_expired_sale",
        label: "No vender productos vencidos",
        hint: "Si lo único que queda de un producto está vencido, el POS avisa al agregarlo y no deja confirmar la venta. La tienda online y los canales (Mercado Libre, Rappi) no se frenan porque el pedido ya está cobrado: descuentan igual, primero lo que vence antes. Las bajas por vencimiento y los ajustes siguen funcionando.",
        icon: <CalendarClock size={14} />,
        requiere: "enable_expiry_tracking",
        requiereLabel: "Vencimientos y lotes",
      },
      {
        // Lógico numérico: prendido = días de aviso; apagado = null (se usan 30).
        id: "expiry_alert_days",
        type: "number",
        defaultValue: 30,
        unit: "días",
        inputLabel: "Avisar con",
        inputSuffix: "de anticipación",
        min: 1,
        max: 365,
        label: "Anticipación del aviso de vencimiento",
        hint: "Desde cuántos días antes un lote aparece \"Por vencer\" en Inventario → Vencimientos. Apagado: 30 días.",
        icon: <CalendarClock size={14} />,
        requiere: "enable_expiry_tracking",
        requiereLabel: "Vencimientos y lotes",
      },
      {
        id: "allow_product_deletion",
        label: "Habilitar eliminar productos",
        hint: "Muestra el botón Eliminar en el detalle del producto. Un producto sin ventas, movimientos de stock, producción ni combos se borra definitivamente.",
        icon: <Trash2 size={14} />,
      },
      {
        id: "allow_product_deletion_with_movements",
        label: "Eliminar productos con movimientos",
        hint: "Permite eliminar un producto que ya tiene historial: ajusta su stock a 0 en todas las sucursales y lo saca del sistema, conservando ventas y kardex.",
        icon: <Trash2 size={14} />,
        requiere: "allow_product_deletion",
        requiereLabel: "Habilitar eliminar productos",
      },
    ],
  },
  {
    id: "produccion",
    label: "Producción y recetas",
    description: "Costos, mermas y órdenes de producción",
    icon: <Hammer size={14} />,
    rules: [
      {
        id: "enable_cost_sync",
        label: "Actualización Costos Recetas",
        hint: "Cuando cambia el costo de un insumo, recalcula en cascada el costo de todas las recetas que lo usan. Sin esto, la receta queda con el costo que tenía cuando se cargó.",
        icon: <Database size={14} />,
      },
      {
        id: "enable_recipe_merma",
        label: "Merma Esperada en Recetas",
        hint: "Agrega el campo de merma esperada (%) en cada receta: el costo unitario se calcula sobre el rendimiento neto, descontando la merma, en vez de sobre la cantidad bruta. Sirve para lo que pierde peso al cocinar o despostar.",
        icon: <TrendingUp size={14} />,
      },
      {
        id: "allow_production_without_stock",
        label: "Producción sin stock de insumos",
        hint: "Viene activo por defecto. Permite registrar una producción aunque los insumos no alcancen: el insumo queda en negativo, que es la deuda real de stock, y se acomoda sola cuando cargás la compra o el ajuste que faltaba. Apagalo si preferís que el sistema frene la producción cuando no hay insumos.",
        icon: <Hammer size={14} />,
      },
    ],
  },
  {
    id: "facturacion",
    label: "Facturación",
    description: "Emisión de comprobantes fiscales",
    icon: <DollarSign size={14} />,
    rules: [
      {
        id: "require_billing_entity_month_limit",
        label: "Exigir límite mensual del emisor",
        hint: "Bloquea la facturación cuando el emisor no tiene cargado un tope para el mes en curso. El tope se carga en Configuración → Facturación → Límites por mes (clave AAAA-MM). Un tope de un mes anterior no habilita el mes actual.",
        icon: <DollarSign size={14} />,
      },
    ],
  },
  {
    id: "clientes",
    label: "Clientes y CRM",
    description: "Ficha de clientes y tableros del CRM",
    icon: <Users size={14} />,
    rules: [
      {
        id: "allow_client_deactivation",
        label: "Habilitar baja de clientes",
        hint: "Muestra la opción de dar de baja un cliente. Deja de aparecer para nuevas ventas, pero conserva su historial y su cuenta corriente.",
        icon: <Power size={14} />,
      },
      {
        id: "enable_crm_kanban",
        label: "Tablero Kanban CRM (Trello)",
        hint: "Habilita el tablero de tarjetas por etapa en los proyectos del CRM, junto con las secciones de tickets y banco de horas que dependen de él. Sin esto, esas tarjetas no se muestran.",
        icon: <FolderKanban size={14} />,
      },
    ],
  },
];

/**
 * EmpresaDatos - Alineado al Modelo Productos
 * Estructura:
 * 1) Header: Título + Acciones
 * 2) KPIs
 * 3) Form (secciones compactas)
 *
 * Mantiene: fetch/update, animación de éxito, toasts.
 *
 * NUEVO:
 * - usd_exchange_rate (cotización USD: ARS por 1 USD)
 * - allow_negative_stock (Permitir vender sin stock)
 * - show_pos_negative_stock (Mostrar el stock en el POS aunque el negativo esté permitido)
 * - enable_misc_product (Permitir item 'Productos Varios' con precio libre)
 * - Integración API Dólar Automática (10 AM diario + Manual)
 * - show_pos_images (Ocultar fotos en el POS para mayor velocidad)
 * - enable_dynamic_combos (Habilita combos por pasos: el cliente elige los productos en el POS)
 */
const EmpresaDatos = () => {
  const { profile, organization, loading: authLoading, refreshOrganization } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [orgPrintConfig, setOrgPrintConfig] = useState({});
  // Qué regla operativa tiene la explicación desplegada (una por vez).
  const [reglaConAyudaAbierta, setReglaConAyudaAbierta] = useState(null);

  // Estado para la carga del dolar
  const [loadingDolar, setLoadingDolar] = useState(false);

  // Fecha de corte del control de arqueos. No se edita desde el formulario: se
  // sella la PRIMERA vez que se activa el logico y despues no se toca, ni al
  // apagarlo. Si se re-sellara al reactivar, los arqueos del periodo apagado
  // desaparecerian del control sin que nadie los haya visto.
  const [arqueoReviewSince, setArqueoReviewSince] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    cuit: "",
    full_address: "",
    phone: "",
    email: "",
    auto_round: false,
    blind_cash_close: false,
    // Cuantos movimientos del turno ve, bajo cierre ciego, quien solo tiene el
    // permiso `caja-ver-ultimos-movimientos`.
    blind_cash_recent_movements: DEFAULT_RECENT_MOVEMENTS_LIMIT,
    alert_arqueo_cero: false,
    enable_arqueo_review: false,
    enable_variants: false,
    enable_variant_stock: false,
    tax_condition: "Responsable Inscripto",
    plan: "free",
    usd_exchange_rate: 0,
    allow_negative_stock: false,
    show_pos_negative_stock: false,
    critical_stock_threshold: null,
    enable_expiry_tracking: false,
    block_expired_sale: false,
    expiry_alert_days: null,
    enable_item_discount: false,
    item_discount_max_pct: null,
    item_discount_reason_from_pct: null,
    allow_zero_sell_price: false,
    enable_misc_product: false,
    defer_non_cf_sales_collection: false,
    enable_cost_sync: false,
    enable_recipe_merma: false, // FIX UX: toggle para activar campo de merma esperada en Recetas
    allow_production_without_stock: false, // Permite registrar produccion aunque el insumo no alcance
    show_pos_images: true,
    use_pos_price_list_per_site: false,
    enable_dynamic_combos: false,
    allow_client_deactivation: false,
    enable_crm_kanban: false,
    // Eliminación de productos. El primero lo maneja la empresa; el segundo lo
    // habilita únicamente el superadmin (ver migración 20260806120000).
    allow_product_deletion: false,
    allow_product_deletion_with_movements: false,
    // Facturación: exige tope mensual cargado en el emisor antes de facturar.
    require_billing_entity_month_limit: false,
    // Lógicos de Rentabilidad (Estado de Resultados / vistas del reporte)
    rentabilidad_incluye_productos: true,
    rentabilidad_incluye_otros_ingresos: false,
    rentabilidad_mostrar_por_producto: true,
    punto_equilibrio_incluye_variables: true,
    pos_ui: DEFAULT_POS_UI_CONFIG,
    system_ui: DEFAULT_SYSTEM_UI_CONFIG,
    mp_access_token: "",
    mp_public_key: "",
    mp_webhook_secret: "",
    getnet_client_id: "",
    getnet_client_secret: "",
    getnet_environment: "sandbox",
  });

  // Carga inicial — los datos vienen del AuthContext (un fetch por sesión).
  useEffect(() => {
    if (organization) {
      hydrateFromOrganization(organization);
      setLoading(false);
    } else if (!authLoading && !profile) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, profile, authLoading]);

  const hydrateFromOrganization = (data) => {
    try {
      if (data) {
        const nextPosUiConfig = getPosUiConfigFromPrintConfig(data.print_config);
        const nextSystemUiConfig = getSystemUiConfigFromPrintConfig(data.print_config);
        setOrgPrintConfig(data.print_config || {});
        setArqueoReviewSince(data.arqueo_review_since || null);

        setFormData({
          name: data.name || "",
          cuit: data.cuit || "",
          full_address: data.full_address || "",
          phone: data.phone || "",
          email: data.email || "",
          auto_round: data.auto_round ?? false,
          blind_cash_close: data.blind_cash_close ?? false,
          blind_cash_recent_movements: resolveRecentMovementsLimit(data.blind_cash_recent_movements),
          alert_arqueo_cero: data.alert_arqueo_cero ?? false,
          enable_arqueo_review: data.enable_arqueo_review ?? false,
          enable_variants: data.enable_variants ?? false,
          enable_variant_stock: data.enable_variant_stock ?? false,
          tax_condition: data.tax_condition || "Responsable Inscripto",
          plan: data.plan || "free",
          usd_exchange_rate: Number(data.usd_exchange_rate || 0),
          allow_negative_stock: data.allow_negative_stock ?? false,
          show_pos_negative_stock: data.show_pos_negative_stock ?? false,
          critical_stock_threshold: data.critical_stock_threshold ?? null,
          enable_expiry_tracking: data.enable_expiry_tracking ?? false,
          block_expired_sale: data.block_expired_sale ?? false,
          expiry_alert_days: data.expiry_alert_days ?? null,
          enable_item_discount: data.enable_item_discount ?? false,
          item_discount_max_pct: data.item_discount_max_pct ?? null,
          item_discount_reason_from_pct: data.item_discount_reason_from_pct ?? null,
          allow_zero_sell_price: data.allow_zero_sell_price ?? false,
          enable_misc_product: data.enable_misc_product ?? false,
          defer_non_cf_sales_collection: data.defer_non_cf_sales_collection ?? false,
          enable_cost_sync: data.enable_cost_sync ?? false,
          enable_recipe_merma: data.enable_recipe_merma ?? false,
          allow_production_without_stock: data.allow_production_without_stock ?? false,
          show_pos_images: data.show_pos_images ?? true,
          use_pos_price_list_per_site: data.use_pos_price_list_per_site ?? false,
          enable_dynamic_combos: data.enable_dynamic_combos ?? false,
          allow_client_deactivation: data.allow_client_deactivation ?? false,
          enable_crm_kanban: data.enable_crm_kanban ?? false,
          allow_product_deletion: data.allow_product_deletion ?? false,
          allow_product_deletion_with_movements: data.allow_product_deletion_with_movements ?? false,
          require_billing_entity_month_limit: data.require_billing_entity_month_limit ?? false,
          rentabilidad_incluye_productos: data.rentabilidad_incluye_productos ?? true,
          rentabilidad_incluye_otros_ingresos: data.rentabilidad_incluye_otros_ingresos ?? false,
          rentabilidad_mostrar_por_producto: data.rentabilidad_mostrar_por_producto ?? true,
          punto_equilibrio_incluye_variables: data.punto_equilibrio_incluye_variables ?? true,
          pos_ui: nextPosUiConfig,
          system_ui: nextSystemUiConfig,
          mp_access_token: data.mp_access_token || "",
          mp_public_key: data.mp_public_key || "",
          mp_webhook_secret: data.mp_webhook_secret || "",
          getnet_client_id: data.getnet_client_id || "",
          getnet_client_secret: data.getnet_client_secret || "",
          getnet_environment: data.getnet_environment || "sandbox",
        });
      }
    } catch (error) {
      console.error(error);
      toast.error("Error de sincronización");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Función para obtener valor del dolar (API Gratuita)
  const fetchDolarValue = useCallback(async (silent = false) => {
    setLoadingDolar(true);
    let toastId;
    if (!silent) toastId = toast.loading("Consultando cotización oficial...");

    try {
      // Usamos dolarapi.com (Gratis, estable y simple)
      const response = await fetch("https://dolarapi.com/v1/dolares/oficial");
      if (!response.ok) throw new Error("Error en API externa");

      const data = await response.json();
      const valorVenta = Number(data.venta || 0);

      if (valorVenta > 0) {
        setFormData(prev => ({ ...prev, usd_exchange_rate: valorVenta }));
        if (!silent) {
          toast.success(`Cotización actualizada: $${valorVenta}`, { id: toastId });
        } else {
          // Aviso discreto si es automático
          toast.success(`Dólar actualizado (10 AM): $${valorVenta}`, { duration: 4000, icon: '💵' });
        }
      } else {
        throw new Error("Valor inválido recibido");
      }
    } catch (error) {
      console.error("Error fetching dolar:", error);
      if (!silent) toast.error("No se pudo obtener la cotización online", { id: toastId });
    } finally {
      setLoadingDolar(false);
    }
  }, []);

  // ✅ EFECTO PROGRAMADO: Ejecutar todos los días a las 10:00 AM
  useEffect(() => {
    const now = new Date();
    const target = new Date();

    // Configurar objetivo: Hoy a las 10:00:00
    target.setHours(10, 0, 0, 0);

    // Si ya pasaron las 10 AM de hoy, programar para mañana
    if (now > target) {
      target.setDate(target.getDate() + 1);
    }

    const msUntilTarget = target.getTime() - now.getTime();

    // Timer inicial hasta las próximas 10 AM
    const timerId = setTimeout(() => {
      fetchDolarValue(true); // Ejecutar actualización (silenciosa)

      // Luego dejar un intervalo de 24 horas (86400000 ms)
      const intervalId = setInterval(() => {
        fetchDolarValue(true);
      }, 24 * 60 * 60 * 1000);

      // Limpiar intervalo interno si el componente se desmonta antes
      return () => clearInterval(intervalId);
    }, msUntilTarget);

    // Limpiar timer inicial
    return () => clearTimeout(timerId);
  }, [fetchDolarValue]);

  const updatePosUiSetting = (key, value) => {
    setFormData((current) => ({
      ...current,
      pos_ui: {
        ...current.pos_ui,
        [key]: value,
      },
    }));
  };

  const updateSystemUiSetting = (key, value) => {
    setFormData((current) => ({
      ...current,
      system_ui: {
        ...current.system_ui,
        [key]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    setIsSuccess(false);

    try {
      const printConfigWithPos = mergePosUiConfigIntoPrintConfig(orgPrintConfig, formData.pos_ui);
      const nextPrintConfig = mergeSystemUiConfigIntoPrintConfig(printConfigWithPos, formData.system_ui);

      // Update defensivo: hay columnas que pueden no existir todavía si la
      // migración SQL correspondiente no fue aplicada en esta base. Postgres
      // (PostgREST) las reporta de a una por vez con código PGRST204, así que
      // las declaramos acá y las vamos descartando en el loop de reintentos.
      // Se sella el corte solo al activar por primera vez. `null` = no tocar la
      // columna en este guardado.
      const arqueoReviewSinceNuevo =
        formData.enable_arqueo_review && !arqueoReviewSince
          ? formatLocalTimestampWithOffset(new Date())
          : null;

      const COLUMNAS_OPCIONALES = [
        {
          key: 'alert_arqueo_cero',
          match: /alert_arqueo_cero/i,
          tip: 'Tip: aplicá la migración SQL para habilitar la alerta de arqueo en cero.',
          apply: (payload) => { payload.alert_arqueo_cero = formData.alert_arqueo_cero; },
        },
        {
          key: 'allow_client_deactivation',
          match: /allow_client_deactivation/i,
          tip: 'Tip: aplicá la migración SQL para habilitar la opción de baja de clientes.',
          apply: (payload) => { payload.allow_client_deactivation = formData.allow_client_deactivation; },
        },
        {
          key: 'enable_recipe_merma',
          match: /enable_recipe_merma/i,
          tip: 'Tip: aplicá la migración SQL para habilitar la opción de merma en recetas.',
          apply: (payload) => { payload.enable_recipe_merma = formData.enable_recipe_merma; },
        },
        {
          key: 'allow_production_without_stock',
          match: /allow_production_without_stock/i,
          tip: 'Tip: aplicá la migración SQL para habilitar la producción sin stock.',
          apply: (payload) => { payload.allow_production_without_stock = formData.allow_production_without_stock; },
        },
        {
          key: 'enable_crm_kanban',
          match: /enable_crm_kanban/i,
          tip: 'Tip: aplicá la migración SQL para habilitar el tablero Kanban del CRM.',
          apply: (payload) => { payload.enable_crm_kanban = formData.enable_crm_kanban; },
        },
        {
          key: 'allow_product_deletion',
          match: /allow_product_deletion(?!_with_movements)/i,
          tip: 'Tip: aplicá la migración SQL para habilitar la eliminación de productos.',
          apply: (payload) => { payload.allow_product_deletion = formData.allow_product_deletion; },
        },
        {
          key: 'allow_product_deletion_with_movements',
          match: /allow_product_deletion_with_movements/i,
          tip: 'Tip: aplicá la migración SQL para habilitar la eliminación de productos con movimientos.',
          // Nunca en true sin el lógico principal: hay un trigger en la base que
          // rechaza esa combinación y abortaría el UPDATE entero del formulario.
          apply: (payload) => {
            payload.allow_product_deletion_with_movements =
              formData.allow_product_deletion &&
              formData.allow_product_deletion_with_movements;
          },
        },
        {
          key: 'require_billing_entity_month_limit',
          match: /require_billing_entity_month_limit/i,
          tip: 'Tip: aplicá la migración SQL para exigir el límite mensual del emisor antes de facturar.',
          apply: (payload) => {
            payload.require_billing_entity_month_limit = formData.require_billing_entity_month_limit;
          },
        },
        {
          key: 'enable_arqueo_review',
          match: /enable_arqueo_review|arqueo_review_since/i,
          tip: 'Tip: aplicá la migración SQL para habilitar el control de arqueos.',
          apply: (payload) => {
            payload.enable_arqueo_review = formData.enable_arqueo_review;
            if (arqueoReviewSinceNuevo) payload.arqueo_review_since = arqueoReviewSinceNuevo;
          },
        },
        {
          key: 'blind_cash_recent_movements',
          match: /blind_cash_recent_movements/i,
          tip: 'Tip: aplicá la migración SQL para configurar cuántos movimientos ve el cajero bajo cierre ciego.',
          apply: (payload) => {
            payload.blind_cash_recent_movements = resolveRecentMovementsLimit(
              formData.blind_cash_recent_movements,
            );
          },
        },
        {
          key: 'show_pos_negative_stock',
          match: /show_pos_negative_stock/i,
          tip: 'Tip: aplicá la migración SQL para mostrar el stock negativo en el POS.',
          apply: (payload) => { payload.show_pos_negative_stock = formData.show_pos_negative_stock; },
        },
        {
          key: 'critical_stock_threshold',
          match: /critical_stock_threshold/i,
          tip: 'Tip: aplicá la migración SQL de notificaciones push para el aviso de stock crítico.',
          apply: (payload) => {
            const v = formData.critical_stock_threshold;
            // null = apagado. Prendido con el campo vacío se guarda 0 (avisar sin stock).
            payload.critical_stock_threshold = v === null || v === undefined
              ? null
              : Math.max(0, Number(v) || 0);
          },
        },
        {
          key: 'expiry_tracking',
          match: /enable_expiry_tracking|block_expired_sale|expiry_alert_days/i,
          tip: 'Tip: aplicá la migración SQL de vencimientos y lotes.',
          apply: (payload) => {
            const activo = Boolean(formData.enable_expiry_tracking);
            payload.enable_expiry_tracking = activo;
            // Sin el lógico principal, los dependientes quedan apagados.
            payload.block_expired_sale = activo && Boolean(formData.block_expired_sale);
            const v = formData.expiry_alert_days;
            payload.expiry_alert_days = !activo || v === null || v === undefined
              ? null
              : Math.min(365, Math.max(1, Number(v) || 30));
          },
        },
        {
          key: 'item_discount',
          match: /enable_item_discount|item_discount_max_pct|item_discount_reason_from_pct/i,
          tip: 'Tip: aplicá la migración SQL de descuento por producto.',
          apply: (payload) => {
            // Numéricos: null = apagado. Prendido con el campo vacío o fuera de
            // rango, se acota a 1..100 (la base tiene un check igual).
            // Prendido con el campo vacío: se guarda el valor por defecto de la regla.
            const pct = (v, porDefecto) => {
              if (v === null || v === undefined) return null;
              const n = v === '' ? porDefecto : Number(v);
              return Math.min(100, Math.max(1, Number.isFinite(n) && n > 0 ? n : porDefecto));
            };
            payload.enable_item_discount = Boolean(formData.enable_item_discount);
            payload.item_discount_max_pct = pct(formData.item_discount_max_pct, 20);
            payload.item_discount_reason_from_pct = pct(formData.item_discount_reason_from_pct, 15);
          },
        },
        {
          key: 'getnet',
          match: /getnet_client_id|getnet_client_secret|getnet_environment/i,
          tip: 'Tip: aplicá la migración SQL de Getnet para habilitar esa pasarela.',
          apply: (payload) => {
            payload.getnet_client_id = formData.getnet_client_id || null;
            payload.getnet_client_secret = formData.getnet_client_secret || null;
            payload.getnet_environment = formData.getnet_environment || 'sandbox';
          },
        },
      ];

      const buildPayload = (opcionalesActivas) => {
        const payload = {
          name: formData.name,
          cuit: formData.cuit,
          full_address: formData.full_address,
          phone: formData.phone,
          email: formData.email,
          auto_round: formData.auto_round,
          blind_cash_close: formData.blind_cash_close,
          enable_variants: formData.enable_variants,
          enable_variant_stock: formData.enable_variant_stock,
          usd_exchange_rate: Number(formData.usd_exchange_rate || 0),
          allow_negative_stock: formData.allow_negative_stock,
          allow_zero_sell_price: formData.allow_zero_sell_price,
          enable_misc_product: formData.enable_misc_product,
          defer_non_cf_sales_collection: formData.defer_non_cf_sales_collection,
          enable_cost_sync: formData.enable_cost_sync,
          show_pos_images: formData.show_pos_images,
          use_pos_price_list_per_site: formData.use_pos_price_list_per_site,
          enable_dynamic_combos: formData.enable_dynamic_combos,
          rentabilidad_incluye_productos: formData.rentabilidad_incluye_productos,
          rentabilidad_incluye_otros_ingresos: formData.rentabilidad_incluye_otros_ingresos,
          rentabilidad_mostrar_por_producto: formData.rentabilidad_mostrar_por_producto,
          punto_equilibrio_incluye_variables: formData.punto_equilibrio_incluye_variables,
          print_config: nextPrintConfig,
          mp_access_token: formData.mp_access_token,
          mp_public_key: formData.mp_public_key,
          mp_webhook_secret: formData.mp_webhook_secret || null,
        };
        COLUMNAS_OPCIONALES
          .filter((col) => opcionalesActivas.has(col.key))
          .forEach((col) => col.apply(payload));
        return payload;
      };

      const opcionalesActivas = new Set(COLUMNAS_OPCIONALES.map((col) => col.key));
      // Columnas que esta base no tiene y hay que sacar del payload. Antes solo
      // se podian descartar las declaradas en COLUMNAS_OPCIONALES: si faltaba
      // cualquier otra (auto_round, blind_cash_close...), el guardado entero se
      // caia y el usuario perdia hasta la razon social y el CUIT. Ahora el
      // nombre de la columna se lee del propio error de PostgREST.
      const columnasAusentes = new Set();

      const buildPayloadSinAusentes = () => {
        const payload = buildPayload(opcionalesActivas);
        columnasAusentes.forEach((col) => { delete payload[col]; });
        return payload;
      };

      // PGRST204 dice: "Could not find the 'x' column of 'organizations' ...".
      const nombreColumnaFaltante = (err) => {
        if (!err || err.code !== 'PGRST204') return null;
        const match = /'([a-z0-9_]+)' column/i.exec(err.message || '');
        return match ? match[1] : null;
      };

      let { error } = await supabase
        .from("organizations")
        .update(buildPayloadSinAusentes())
        .eq("id", profile.organization_id);

      // Reintentamos una vez por columna faltante (PGRST204 reporta de a una).
      // El tope evita un bucle infinito si el mensaje cambia de formato.
      for (let intento = 0; intento < 25; intento += 1) {
        const faltante = nombreColumnaFaltante(error);
        if (!faltante || columnasAusentes.has(faltante)) break;

        columnasAusentes.add(faltante);
        opcionalesActivas.delete(faltante);
        // Aviso solo para la consola: "aplicá la migración SQL" es un mensaje
        // para quien mantiene el sistema, no para el comerciante.
        console.warn(`[EmpresaDatos] La columna ${faltante} no existe en esta base; se guarda sin ella.`);

        const retry = await supabase
          .from("organizations")
          .update(buildPayloadSinAusentes())
          .eq("id", profile.organization_id);
        error = retry.error;
      }

      if (error) throw error;

      setIsSuccess(true);
      if (arqueoReviewSinceNuevo && !columnasAusentes.has('arqueo_review_since')) {
        setArqueoReviewSince(arqueoReviewSinceNuevo);
      }
      setOrgPrintConfig(nextPrintConfig);
      broadcastSystemUiConfigChange(formData.system_ui);
      // Refrescamos la org en el AuthContext para que las páginas que la
      // consumen (POS, Facturación, etc.) vean los cambios sin recarga.
      refreshOrganization?.();
      toast.success("Nodo actualizado");

      setTimeout(() => setIsSuccess(false), 2000);
    } catch (error) {
      console.error(error);
      toast.error(error?.message ? `No se pudo guardar: ${error.message}` : 'No se pudo guardar. Revisá la conexión y volvé a intentar.');
    } finally {
      setSaving(false);
    }
  };

  const kpis = useMemo(() => {
    const completos =
      Boolean(formData.name?.trim()) &&
      Boolean(formData.cuit?.trim()) &&
      Boolean(formData.full_address?.trim()) &&
      Boolean(formData.email?.trim()) &&
      Boolean(formData.phone?.trim());

    const togglesOn = [
      formData.auto_round,
      formData.blind_cash_close,
      formData.alert_arqueo_cero,
      formData.enable_arqueo_review,
      formData.enable_variants,
      formData.enable_variant_stock,
      formData.allow_negative_stock,
      formData.show_pos_negative_stock, // ✅ Ver stock en el POS con negativo permitido
      formData.critical_stock_threshold !== null && formData.critical_stock_threshold !== undefined, // Aviso push de stock crítico
      formData.enable_expiry_tracking, // Vencimientos y lotes
      formData.enable_expiry_tracking && formData.block_expired_sale, // No vender vencidos
      formData.allow_zero_sell_price,
      formData.enable_misc_product,
      formData.defer_non_cf_sales_collection,
      formData.enable_cost_sync,
      formData.enable_recipe_merma, // Toggle de merma esperada en recetas
      formData.allow_production_without_stock, // Produccion sin stock de insumos
      formData.show_pos_images, // ✅ AGREGADO AL CONTADOR
      formData.use_pos_price_list_per_site, // ✅ Lista de precios por sitio
      formData.enable_dynamic_combos, // ✅ Combos dinámicos
      formData.enable_item_discount, // Descuento por producto
      formData.allow_client_deactivation, // ✅ Baja de clientes
      formData.enable_crm_kanban, // ✅ Tablero Kanban CRM (Trello)
      formData.allow_product_deletion, // ✅ Eliminar productos
      formData.allow_product_deletion_with_movements, // ✅ Eliminar productos con movimientos
      formData.require_billing_entity_month_limit // ✅ Exigir límite mensual del emisor
    ].filter(Boolean).length;

    const posPreferencesOn = [
      formData.pos_ui.startCollapsedSaleContext,
      formData.pos_ui.useCategoryDropdown,
      formData.pos_ui.showSearchBar,
      formData.pos_ui.showHeader,
      formData.pos_ui.showProductBrand,
      formData.pos_ui.abbreviateProductNames,
      formData.pos_ui.sendPosSalesToLogistics,
      formData.pos_ui.allowCartPriceEdit,
      formData.pos_ui.allowDecimalCartQuantity,
    ].filter(Boolean).length;

    const cuitOk = (formData.cuit || "").replace(/\D/g, "").length >= 11;

    return {
      completos,
      togglesOn: togglesOn + posPreferencesOn,
      cuitOk,
      usdRate: Number(formData.usd_exchange_rate || 0),
      usdRateOk: Number(formData.usd_exchange_rate || 0) > 0,
      plan: formData.plan || "free",
    };
  }, [formData]);

  if (loading || authLoading) {
    return (
      <div className="imp-page imp-container">
        <div className="h-full flex flex-col items-center justify-center p-8">
          <div className="w-11 h-11 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center mb-3">
            <RefreshCcw className="text-slate-300 animate-spin" size={18} />
          </div>
          <p className="font-black uppercase tracking-[0.28em] text-slate-400 text-[10px]">Sincronizando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="imp-page imp-container animate-nova-in">

      {/* HEADER ESTILO SISTEMA - TIPO PRODUCTOS */}
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Settings2 size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Mi Empresa</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Configuración del nodo · Identidad fiscal y reglas de operación
            </p>
          </div>
        </div>
      </div>

      {/* KPI (Modelo Productos) */}
      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Plan Actual</p>
            <Building2 size={16} className="text-[#51A7FD]" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">
            {String(kpis.plan).toUpperCase()}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Nivel de servicio</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Cotización USD</p>
            <DollarSign size={16} className={kpis.usdRateOk ? "text-emerald-500" : "text-amber-500"} />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">
            {kpis.usdRateOk ? `$ ${Number(kpis.usdRate).toLocaleString("es-AR")}` : "-"}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Tipo de cambio ERP</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Ficha completa</p>
            <Info size={16} className={kpis.completos ? "text-emerald-500" : "text-amber-500"} />
          </div>
          <p className={`mt-1.5 text-2xl font-black tracking-tight ${kpis.completos ? "text-emerald-600" : "text-amber-600"}`}>
            {kpis.completos ? "OK" : "Pendiente"}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Datos fiscales</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Reglas activas</p>
            <Layers size={16} className="text-purple-500" />
          </div>
          {/* ✅ ACTUALIZADO A 7 PARA INCLUIR EL NUEVO TOGGLE */}
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.togglesOn} / 20</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Configuraciones</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* SECCIÓN: IDENTIDAD (compacta, estilo productos) */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400 border border-slate-100">
              <Building2 size={14} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-700 uppercase tracking-tight">Datos de la empresa</p>
              <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                Información {kpis.cuitOk ? "empresarial" : "pendiente/incorrecto"}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Razón social
              </label>
              <input
                data-onboarding-id="business-name-input"
                type="text"
                className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner text-slate-700"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="space-y-1">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                CUIT
              </label>
              <input
                data-onboarding-id="business-cuit-input"
                type="text"
                className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner text-slate-700"
                value={formData.cuit}
                onChange={(e) => setFormData({ ...formData, cuit: e.target.value })}
              />
            </div>

            <div className="space-y-1 md:col-span-2">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Dirección fiscal
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                <input
                  data-onboarding-id="business-address-input"
                  type="text"
                  className="w-full bg-slate-50 border border-slate-100 pl-9 pr-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner text-slate-700"
                  value={formData.full_address}
                  onChange={(e) => setFormData({ ...formData, full_address: e.target.value })}
                />
              </div>
            </div>
          </div>
        </div>

        {/* SECCIÓN: CONTACTO */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400 border border-slate-100">
              <Mail size={14} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-700 uppercase tracking-tight">Comunicación</p>
              <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                Datos para contacto administrativo
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Email administrativo
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                <input
                  type="email"
                  className="w-full bg-slate-50 border border-slate-100 pl-9 pr-3 py-2.5 rounded-xl text-[10px] font-bold lowercase outline-none focus:border-[#51A7FD] transition-all shadow-inner text-slate-700"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Teléfono / WhatsApp
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                <input
                  type="text"
                  className="w-full bg-slate-50 border border-slate-100 pl-9 pr-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner text-slate-700"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>
          </div>
        </div>

        {/* SECCIÓN: REGLAS OPERATIVAS — agrupadas por donde aplica cada lógico */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400 border border-slate-100">
              <Hash size={14} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-700 uppercase tracking-tight">Reglas operativas</p>
              <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                Agrupadas por el módulo donde aplican · tocá el ícono para ver qué hace cada una
              </p>
            </div>
          </div>

          {REGLAS_OPERATIVAS.map((grupo) => (
            <div key={grupo.id} className="mt-4 pt-3 border-t border-slate-100 first-of-type:border-t-0">
              <div className="flex items-center gap-2 mb-2.5">
                <div className="w-7 h-7 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400 border border-slate-100 shrink-0">
                  {grupo.icon}
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-black text-slate-700 uppercase tracking-tight">{grupo.label}</p>
                  <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest truncate">{grupo.description}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {grupo.rules.map((rule) => {
                  const esNumerico = rule.type === "number";
                  const enabled = esNumerico
                    ? formData[rule.id] !== null && formData[rule.id] !== undefined
                    : Boolean(formData[rule.id]);
                  // Un lógico que depende de otro queda deshabilitado hasta que el
                  // primero esté prendido. El padre puede estar en otro grupo: el
                  // nombre viaja en la propia regla (`requiereLabel`).
                  const bloqueado = Boolean(rule.requiere) && !formData[rule.requiere];
                  const nombrePadre = rule.requiereLabel || rule.requiere;
                  const ayudaAbierta = reglaConAyudaAbierta === rule.id;
                  const textoAyuda = bloqueado
                    ? `${rule.hint} Requiere habilitar antes “${nombrePadre}”.`
                    : rule.hint;

                  const toggleRegla = () => {
                    if (bloqueado) {
                      toast.error(`Primero habilitá “${nombrePadre}”.`);
                      return;
                    }
                    setFormData((actual) => {
                      // El lógico numérico se prende con su valor por defecto y se apaga con null.
                      const valor = esNumerico ? (enabled ? null : rule.defaultValue) : !enabled;
                      const siguiente = { ...actual, [rule.id]: valor };
                      // Al apagar un lógico, se apagan los que dependían de él:
                      // guardar la combinación incoherente lo rechaza el trigger.
                      if (rule.id === "allow_product_deletion" && enabled) {
                        siguiente.allow_product_deletion_with_movements = false;
                      }
                      if (rule.id === "enable_expiry_tracking" && enabled) {
                        siguiente.block_expired_sale = false;
                        siguiente.expiry_alert_days = null;
                      }
                      return siguiente;
                    });
                  };

                  return (
                    <div
                      key={rule.id}
                      className={`p-3 rounded-xl border transition-all ${bloqueado ? "opacity-60 " : ""}${
                        enabled ? "bg-blue-50 border-blue-100" : "bg-slate-50 border-slate-100"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <button
                          type="button"
                          onClick={toggleRegla}
                          title={textoAyuda}
                          className={`flex items-center gap-2 min-w-0 flex-1 text-left ${bloqueado ? "cursor-not-allowed" : ""}`}
                        >
                          <div
                            className={`p-1.5 rounded-lg shrink-0 ${
                              enabled ? "bg-blue-500 text-white shadow-sm" : "bg-white text-slate-400 shadow-sm"
                            }`}
                          >
                            {rule.icon}
                          </div>
                          <p className="text-[9px] font-black uppercase tracking-tight text-slate-700 flex items-center gap-1">
                            {rule.label}
                            {bloqueado && <Lock size={9} className="text-slate-400 shrink-0" />}
                          </p>
                        </button>

                        <div className="flex items-center gap-1.5 shrink-0">
                          {/* En celular no hay hover: la explicación se abre acá abajo. */}
                          <button
                            type="button"
                            onClick={() => setReglaConAyudaAbierta(ayudaAbierta ? null : rule.id)}
                            title={textoAyuda}
                            aria-expanded={ayudaAbierta}
                            aria-label={`Qué hace “${rule.label}”`}
                            className={`p-1 rounded-lg transition-colors ${
                              ayudaAbierta ? "bg-white text-[#51A7FD] shadow-sm" : "text-slate-300 hover:text-slate-500"
                            }`}
                          >
                            <Info size={13} />
                          </button>

                          {/* Estilo unificado para los toggles */}
                          <button
                            type="button"
                            onClick={toggleRegla}
                            aria-pressed={enabled}
                            aria-label={enabled ? "Activado" : "Desactivado"}
                            className={`w-10 h-5 rounded-full transition-all relative ${bloqueado ? "cursor-not-allowed" : ""} ${
                              enabled ? "bg-[#3ff2aa]" : "bg-slate-300"
                            }`}
                          >
                            <div
                              className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${
                                enabled ? "left-6" : "left-1"
                              }`}
                            />
                          </button>
                        </div>
                      </div>

                      {esNumerico && enabled && (
                        <label className="mt-2 pt-2 border-t border-white/60 flex items-center justify-between gap-2">
                          <span className="text-[9px] font-bold text-slate-500 uppercase tracking-tight">{rule.inputLabel || "Avisar con"}</span>
                          <span className="flex items-center gap-1.5">
                            <input
                              type="number"
                              min={rule.min ?? 0}
                              max={rule.max}
                              step="1"
                              inputMode="numeric"
                              value={formData[rule.id] ?? ""}
                              onChange={(e) => {
                                const crudo = e.target.value;
                                setFormData((actual) => ({
                                  ...actual,
                                  // Vacío mientras se escribe; al guardar se acota al rango de la regla.
                                  [rule.id]: crudo === ""
                                    ? ""
                                    : Math.min(rule.max ?? Infinity, Math.max(rule.min ?? 0, Number(crudo))),
                                }));
                              }}
                              className="w-16 px-2 py-1 rounded-lg border border-blue-200 bg-white text-xs font-bold text-slate-700 text-right"
                            />
                            <span className="text-[9px] font-bold text-slate-500">{rule.unit} {rule.inputSuffix || "o menos"}</span>
                          </span>
                        </label>
                      )}

                      {ayudaAbierta && (
                        <p className="mt-2 pt-2 border-t border-white/60 text-[9px] font-bold text-slate-500 leading-relaxed normal-case">
                          {textoAyuda}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Cierre ciego: cuántos movimientos ve el cajero con permiso acotado */}
              {grupo.id === "caja" && formData.blind_cash_close && (
                <div className="mt-3 p-4 rounded-xl border border-slate-200 bg-slate-50/50">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-slate-400 border border-slate-200 shadow-sm">
                      <Calculator size={14} />
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-slate-700 uppercase tracking-tight">Cierre ciego: movimientos visibles</p>
                      <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                        Cuántos movimientos ve quien tiene el permiso “Caja: Ver Últimos Movimientos”
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min={1}
                      max={MAX_RECENT_MOVEMENTS_LIMIT}
                      step={1}
                      value={formData.blind_cash_recent_movements}
                      onChange={(e) =>
                        setFormData({ ...formData, blind_cash_recent_movements: e.target.value })
                      }
                      onBlur={(e) =>
                        setFormData({
                          ...formData,
                          blind_cash_recent_movements: resolveRecentMovementsLimit(e.target.value),
                        })
                      }
                      className="w-24 px-3 py-2 bg-white border border-slate-200 rounded-xl text-[11px] font-black text-slate-700 outline-none focus:border-[#51A7FD] transition-all"
                    />
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                      Entre 1 y {MAX_RECENT_MOVEMENTS_LIMIT} · Por defecto {DEFAULT_RECENT_MOVEMENTS_LIMIT}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Rentabilidad: qué bloques muestra el Estado de Resultados */}
          <div className="mt-4 p-4 rounded-xl border border-slate-200 bg-slate-50/50">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-slate-400 border border-slate-200 shadow-sm">
                <TrendingUp size={14} />
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-700 uppercase tracking-tight">Rentabilidad</p>
                <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                  Qué bloques muestra el reporte de Rentabilidad
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { id: "rentabilidad_incluye_productos", label: "Mostrar productos (Ventas, CMV, Margen Bruto)", icon: <Package size={14} /> },
                { id: "rentabilidad_mostrar_por_producto", label: "Mostrar rentabilidad por producto (listado)", icon: <Layers size={14} /> },
                { id: "rentabilidad_incluye_otros_ingresos", label: "Incluir servicios (ingresos y gastos por servicios)", icon: <TrendingUp size={14} /> },
                {
                  id: "punto_equilibrio_incluye_variables",
                  label: "Punto de equilibrio: restar gastos variables",
                  hint: "Apagalo si cargás compras de mercadería como gasto variable: ya están en el CMV y se contarían dos veces.",
                  icon: <Scale size={14} />,
                },
              ].map((rule) => {
                const enabled = Boolean(formData[rule.id]);
                return (
                  <button
                    key={rule.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, [rule.id]: !enabled })}
                    className={`p-3 rounded-xl border transition-all text-left flex items-center justify-between ${enabled ? "bg-blue-50 border-blue-100" : "bg-white border-slate-100"}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`p-1.5 rounded-lg ${enabled ? "bg-blue-500 text-white shadow-sm" : "bg-white text-slate-400 shadow-sm"}`}>
                        {rule.icon}
                      </div>
                      <div>
                        <p className="text-[9px] font-black uppercase tracking-tight text-slate-700">
                          {rule.label}
                        </p>
                        {rule.hint && (
                          <p className="text-[8px] font-bold text-slate-400 normal-case leading-snug mt-0.5">{rule.hint}</p>
                        )}
                      </div>
                    </div>
                    <div className={`w-10 h-5 rounded-full transition-all relative shrink-0 ${enabled ? "bg-[#3ff2aa]" : "bg-slate-300"}`}>
                      <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${enabled ? "left-6" : "left-1"}`} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Cotización USD (ARS por 1 USD) */}
          <div className="mt-4 p-4 rounded-xl border border-slate-200 bg-slate-50/50">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-slate-400 border border-slate-200 shadow-sm">
                <DollarSign size={14} />
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-700 uppercase tracking-tight">Cotización USD</p>
                <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                  ARS por 1 USD · se usa para productos con precio en dólares
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div className="space-y-1 md:col-span-1">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  USD (ARS)
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    className="w-full bg-white border border-slate-200 px-3 py-2.5 rounded-xl text-[10px] font-black uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner text-slate-700"
                    value={formData.usd_exchange_rate}
                    onChange={(e) =>
                      setFormData({ ...formData, usd_exchange_rate: Number(e.target.value || 0) })
                    }
                    placeholder="0"
                  />
                  {/* BOTON SINCRONIZAR API (MANUAL) */}
                  <button
                    type="button"
                    onClick={() => fetchDolarValue(false)}
                    disabled={loadingDolar}
                    className="w-10 h-10 flex items-center justify-center bg-blue-100 text-blue-600 rounded-xl hover:bg-blue-200 transition-colors shadow-sm disabled:opacity-50"
                    title="Actualizar con Dolar Oficial (API)"
                  >
                    {loadingDolar ? (
                      <RefreshCcw size={14} className="animate-spin" />
                    ) : (
                      <TrendingUp size={16} />
                    )}
                  </button>
                </div>
              </div>

              <div className="md:col-span-2">
                <div
                  className={`p-3 rounded-xl border ${kpis.usdRateOk ? "bg-emerald-50 border-emerald-100" : "bg-amber-50 border-amber-100"
                    }`}
                >
                  <p className={`text-[9px] font-black uppercase ${kpis.usdRateOk ? "text-emerald-700" : "text-amber-700"}`}>
                    {kpis.usdRateOk
                      ? `Cotización activa: $${Number(kpis.usdRate).toLocaleString("es-AR")} ARS por 1 USD.`
                      : "Sin cotización: los productos con USD podrán guardarse, pero el precio ARS quedará manual."}
                  </p>
                  <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                    Actualización automática activa (10 AM) o manual con el botón.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 px-1">
            <Info className="text-[#51A7FD]" size={14} />
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest italic">
              Cambios fiscales impactan en tiempo real (y sí, también en soporte).
            </p>
          </div>
        </div>

        {/* SECCIÓN: INTEGRACIONES (MERCADO PAGO) */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-[#51A7FD] border border-blue-100">
              <Database size={14} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-700 uppercase tracking-tight">Pasarela de Pagos (Mercado Pago)</p>
              <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                Credenciales PRO para Cobro Integrado sin salir de la Tienda Virtual
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Access Token (Backend)
              </label>
              <input
                type="password"
                className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold outline-none focus:border-[#51A7FD] transition-all shadow-inner text-slate-700 font-mono"
                value={formData.mp_access_token}
                onChange={(e) => setFormData({ ...formData, mp_access_token: e.target.value })}
                placeholder="APP_USR-..."
              />
            </div>
            <div className="space-y-1">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Public Key (Frontend - Bricks)
              </label>
              <input
                type="text"
                className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold outline-none focus:border-[#51A7FD] transition-all shadow-inner text-slate-700 font-mono"
                value={formData.mp_public_key}
                onChange={(e) => setFormData({ ...formData, mp_public_key: e.target.value })}
                placeholder="APP_USR-... (Llave Pública)"
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Clave Secreta del Webhook (opcional)
              </label>
              <input
                type="password"
                className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold outline-none focus:border-[#51A7FD] transition-all shadow-inner text-slate-700 font-mono"
                value={formData.mp_webhook_secret}
                onChange={(e) => setFormData({ ...formData, mp_webhook_secret: e.target.value })}
                placeholder="Secret del webhook en MP Developers (refuerza la seguridad)"
              />
            </div>
          </div>
          <div className="mt-3 p-3 bg-slate-50 rounded-xl border border-slate-100 flex items-start gap-2">
             <Info size={14} className="text-slate-400 mt-0.5" />
             <p className="text-[9px] font-bold text-slate-500 leading-relaxed uppercase tracking-wide">
                Configura esto SOLO si planeas usar el Checkout Pro/Bricks. Evita compartir este Access Token con terceros. Si lo modificas, asegúrate de actualizar Webhooks en el panel de desarrolladores de MP.
             </p>
          </div>
        </div>

        {/* SECCIÓN: INTEGRACIONES (GETNET / GEOPAGOS) */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center text-rose-500 border border-rose-100">
              <Database size={14} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-700 uppercase tracking-tight">Pasarela de Pagos (Getnet)</p>
              <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                Credenciales OAuth de Getnet (Santander / Geopagos) para cobro en POS y tienda online
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Client ID
              </label>
              <input
                type="text"
                className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold outline-none focus:border-rose-400 transition-all shadow-inner text-slate-700 font-mono"
                value={formData.getnet_client_id}
                onChange={(e) => setFormData({ ...formData, getnet_client_id: e.target.value })}
                placeholder="Client ID provisto por Getnet"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Client Secret (Backend)
              </label>
              <input
                type="password"
                className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold outline-none focus:border-rose-400 transition-all shadow-inner text-slate-700 font-mono"
                value={formData.getnet_client_secret}
                onChange={(e) => setFormData({ ...formData, getnet_client_secret: e.target.value })}
                placeholder="Client Secret provisto por Getnet"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Ambiente
              </label>
              <select
                className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold outline-none focus:border-rose-400 transition-all shadow-inner text-slate-700 uppercase cursor-pointer"
                value={formData.getnet_environment}
                onChange={(e) => setFormData({ ...formData, getnet_environment: e.target.value })}
              >
                <option value="sandbox">SANDBOX (PRUEBAS)</option>
                <option value="production">PRODUCCIÓN</option>
              </select>
            </div>
          </div>
          <div className="mt-3 p-3 bg-slate-50 rounded-xl border border-slate-100 flex items-start gap-2">
             <Info size={14} className="text-slate-400 mt-0.5" />
             <p className="text-[9px] font-bold text-slate-500 leading-relaxed uppercase tracking-wide">
                El Client Secret nunca se expone a la tienda: solo lo usan las funciones de servidor. El modo de cobro (QR presencial o terminal SmartPOS) se elige por cada Método de Cobro.
             </p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400 border border-slate-100">
              <Monitor size={14} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-700 uppercase tracking-tight">Punto de venta</p>
              <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                Preferencias visuales para catalogo, cabecera y espacio operativo
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {[
              {
                id: "startCollapsedSaleContext",
                label: "Iniciar bloque de venta minimizado",
                description: "Arranca compacto y permite desplegar Documento, Cliente, Sucursal y Fecha con una flecha.",
                icon: <Layers size={14} />,
              },
              {
                id: "useCategoryDropdown",
                label: "Categorias en desplegable",
                description: "Reemplaza la tira de categorias por un selector compacto cuando el catalogo es muy grande.",
                icon: <Layers size={14} />,
              },
              {
                id: "showSearchBar",
                label: "Mostrar buscador EAN",
                description: "Muestra u oculta el bloque ESCANEAR EAN O BUSCAR del POS.",
                icon: <Search size={14} />,
              },
              {
                id: "showHeader",
                label: "Mostrar cabecera del POS",
                description: "Muestra u oculta el icono y los textos Punto de Venta / Terminal de Facturacion.",
                icon: <Monitor size={14} />,
              },
              {
                id: "showProductBrand",
                label: "Mostrar marca en POS",
                description: "Muestra la marca arriba del nombre de cada producto en las tarjetas del catalogo.",
                icon: <Award size={14} />,
              },
              {
                id: "abbreviateProductNames",
                label: "Abreviar nombres de productos",
                description: "Si lo activas, los nombres largos se recortan como hoy. Si lo apagas, se muestran completos.",
                icon: <Hash size={14} />,
              },
              {
                id: "show_pos_images",
                label: "Mostrar fotos en POS",
                description: "Si lo apagas, el catalogo carga mas liviano y deja mas foco en el listado.",
                icon: <ImageIcon size={14} />,
                rootKey: "show_pos_images",
              },
              {
                id: "sendPosSalesToLogistics",
                label: "Enviar ventas POS a logistica",
                description: "Cada venta real del mostrador entra tambien a la tarjeta de pedidos para programar entrega o retiro.",
                icon: <Package size={14} />,
              },
              {
                id: "allowCartPriceEdit",
                label: "Editar precios en carrito",
                description: "Permite ajustar el precio unitario de productos ya agregados dentro del carrito del POS.",
                icon: <DollarSign size={14} />,
              },
              {
                id: "allowDecimalCartQuantity",
                label: "Cantidades decimales en carrito",
                description: "Permite ingresar cantidades con coma o punto y hasta 3 decimales en el carrito del POS, ideal para venta a granel.",
                icon: <Calculator size={14} />,
              },
            ].map((rule) => {
              const enabled = rule.rootKey
                ? Boolean(formData[rule.rootKey])
                : Boolean(formData.pos_ui[rule.id]);

              const handleToggle = () => {
                if (rule.rootKey) {
                  setFormData((current) => ({ ...current, [rule.rootKey]: !enabled }));
                  return;
                }

                updatePosUiSetting(rule.id, !enabled);
              };

              return (
                <button
                  key={rule.id}
                  type="button"
                  onClick={handleToggle}
                  className={`p-3 rounded-xl border transition-all text-left ${enabled ? "bg-blue-50 border-blue-100" : "bg-slate-50 border-slate-100"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2 min-w-0">
                      <div className={`mt-0.5 p-1.5 rounded-lg ${enabled ? "bg-blue-500 text-white shadow-sm" : "bg-white text-slate-400 shadow-sm"}`}>
                        {rule.icon}
                      </div>
                      <div className="min-w-0">
                        <p className="text-[9px] font-black uppercase tracking-tight text-slate-700">
                          {rule.label}
                        </p>
                        <p className="mt-1 text-[9px] font-bold text-slate-500 leading-relaxed normal-case tracking-normal">
                          {rule.description}
                        </p>
                      </div>
                    </div>

                    <div
                      className={`w-10 h-5 rounded-full transition-all relative shrink-0 ${enabled ? "bg-[#3ff2aa]" : "bg-slate-300"}`}
                      aria-label={enabled ? "Activado" : "Desactivado"}
                    >
                      <div
                        className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${enabled ? "left-6" : "left-1"}`}
                      />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-4 p-4 rounded-xl border border-slate-200 bg-slate-50/60">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-black text-slate-700 uppercase tracking-tight">Zoom operativo del POS</p>
                <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                  Ajusta la escala visual del modulo desde 50% hasta 150%
                </p>
              </div>
              <div className="px-3 py-1 rounded-xl bg-white border border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-600 shrink-0">
                {formData.pos_ui.zoomPercent}%
              </div>
            </div>

            <div className="mt-4">
              <input
                type="range"
                min={50}
                max={150}
                step={10}
                value={formData.pos_ui.zoomPercent}
                onChange={(e) => updatePosUiSetting("zoomPercent", Number(e.target.value))}
                className="w-full accent-[#0B2A36]"
              />
              <div className="mt-2 flex flex-wrap items-center gap-2 text-[8px] font-black uppercase tracking-widest text-slate-400">
                {POS_ZOOM_OPTIONS.map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => updatePosUiSetting("zoomPercent", value)}
                    className={`px-2 py-1 rounded-lg transition-all ${formData.pos_ui.zoomPercent === value ? "bg-[#0B2A36] text-white" : "bg-white border border-slate-200 text-slate-500 hover:text-slate-700"}`}
                  >
                    {value}%
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-3 flex items-start gap-2">
              <Info size={14} className="text-slate-300 mt-0.5" />
              <p className="text-[9px] font-bold text-slate-500 leading-relaxed">
                El zoom modifica la escala del POS completo. En 100% queda igual que hoy; en 50% entra mas catalogo y en 150% se prioriza legibilidad.
              </p>
            </div>
          </div>
        </div>

        {/* ===================== ZOOM RESPONSIVE DEL SISTEMA ===================== */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400 border border-slate-100">
              <Monitor size={14} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-700 uppercase tracking-tight">Interfaz del sistema</p>
              <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                Ajusta el zoom del ERP segun el tamano de pantalla para evitar scroll horizontal
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => updateSystemUiSetting("enableResponsiveZoom", !formData.system_ui.enableResponsiveZoom)}
            className={`w-full p-3 rounded-xl border transition-all text-left ${formData.system_ui.enableResponsiveZoom ? "bg-blue-50 border-blue-100" : "bg-slate-50 border-slate-100"}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 min-w-0">
                <div className={`mt-0.5 p-1.5 rounded-lg ${formData.system_ui.enableResponsiveZoom ? "bg-blue-500 text-white shadow-sm" : "bg-white text-slate-400 shadow-sm"}`}>
                  <Settings2 size={14} />
                </div>
                <div className="min-w-0">
                  <p className="text-[9px] font-black uppercase tracking-tight text-slate-700">
                    Activar zoom adaptativo por tamano de pantalla
                  </p>
                  <p className="mt-1 text-[9px] font-bold text-slate-500 leading-relaxed normal-case tracking-normal">
                    Cuando esta activo, el sistema escala su interfaz al porcentaje que definas para cada tipo de pantalla. Util si en notebooks o tablets se ve muy grande y aparece scroll horizontal.
                  </p>
                </div>
              </div>

              <div
                className={`w-10 h-5 rounded-full transition-all relative shrink-0 ${formData.system_ui.enableResponsiveZoom ? "bg-[#3ff2aa]" : "bg-slate-300"}`}
                aria-label={formData.system_ui.enableResponsiveZoom ? "Activado" : "Desactivado"}
              >
                <div
                  className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${formData.system_ui.enableResponsiveZoom ? "left-6" : "left-1"}`}
                />
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => updateSystemUiSetting("keepFontsLarge", !formData.system_ui.keepFontsLarge)}
            disabled={!formData.system_ui.enableResponsiveZoom}
            className={`mt-3 w-full p-3 rounded-xl border transition-all text-left ${!formData.system_ui.enableResponsiveZoom ? "opacity-50 pointer-events-none bg-slate-50 border-slate-100" : formData.system_ui.keepFontsLarge ? "bg-blue-50 border-blue-100" : "bg-slate-50 border-slate-100"}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 min-w-0">
                <div className={`mt-0.5 p-1.5 rounded-lg ${formData.system_ui.keepFontsLarge ? "bg-blue-500 text-white shadow-sm" : "bg-white text-slate-400 shadow-sm"}`}>
                  <Hash size={14} />
                </div>
                <div className="min-w-0">
                  <p className="text-[9px] font-black uppercase tracking-tight text-slate-700">
                    Mantener fuentes legibles al achicar
                  </p>
                  <p className="mt-1 text-[9px] font-bold text-slate-500 leading-relaxed normal-case tracking-normal">
                    Cuando el zoom es menor a 100%, sube proporcionalmente el tamano de fuente para que el texto se vea igual de grande que sin zoom. Solo achican padding, gap, iconos y bordes. Util si entra mas info pero la pantalla se ve borrosa o muy chica.
                  </p>
                </div>
              </div>

              <div
                className={`w-10 h-5 rounded-full transition-all relative shrink-0 ${formData.system_ui.keepFontsLarge ? "bg-[#3ff2aa]" : "bg-slate-300"}`}
                aria-label={formData.system_ui.keepFontsLarge ? "Activado" : "Desactivado"}
              >
                <div
                  className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${formData.system_ui.keepFontsLarge ? "left-6" : "left-1"}`}
                />
              </div>
            </div>
          </button>

          <div className={`mt-4 grid gap-3 md:grid-cols-3 transition-opacity ${formData.system_ui.enableResponsiveZoom ? "opacity-100" : "opacity-50 pointer-events-none"}`}>
            {[
              { key: "mobileZoomPercent", label: "Movil", hint: "< 768 px" },
              { key: "tabletZoomPercent", label: "Tablet / Notebook", hint: "768 - 1279 px" },
              { key: "desktopZoomPercent", label: "Desktop", hint: ">= 1280 px" },
            ].map((bp) => (
              <div key={bp.key} className="p-4 rounded-xl border border-slate-200 bg-slate-50/60">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-[10px] font-black text-slate-700 uppercase tracking-tight">{bp.label}</p>
                    <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{bp.hint}</p>
                  </div>
                  <div className="px-2.5 py-0.5 rounded-xl bg-white border border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-600 shrink-0">
                    {formData.system_ui[bp.key]}%
                  </div>
                </div>

                <input
                  type="range"
                  min={50}
                  max={150}
                  step={10}
                  value={formData.system_ui[bp.key]}
                  onChange={(e) => updateSystemUiSetting(bp.key, Number(e.target.value))}
                  disabled={!formData.system_ui.enableResponsiveZoom}
                  className="w-full accent-[#0B2A36] mt-3"
                />

                <div className="mt-2 flex flex-wrap gap-1 text-[8px] font-black uppercase tracking-widest text-slate-400">
                  {SYSTEM_ZOOM_OPTIONS.map((value) => (
                    <button
                      key={value}
                      type="button"
                      disabled={!formData.system_ui.enableResponsiveZoom}
                      onClick={() => updateSystemUiSetting(bp.key, value)}
                      className={`px-1.5 py-0.5 rounded transition-all ${formData.system_ui[bp.key] === value ? "bg-[#0B2A36] text-white" : "bg-white border border-slate-200 text-slate-500 hover:text-slate-700"}`}
                    >
                      {value}%
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3 flex items-start gap-2">
            <Info size={14} className="text-slate-300 mt-0.5" />
            <p className="text-[9px] font-bold text-slate-500 leading-relaxed">
              Probalo en 80-90% para notebooks chicas (1366 px) que muestran scroll horizontal. Si activas tambien &ldquo;Mantener fuentes legibles&rdquo;, el layout sigue achicandose pero el texto compensa para verse al tamano original; util cuando el zoom hace el contenido dificil de leer. El cambio se aplica al guardar.
            </p>
          </div>
        </div>

        {/* Acciones (abajo, a la derecha) */}
        <div className="mt-6 flex justify-end">
          <button
            data-onboarding-id="business-submit"
            type="button"
            onClick={handleSubmit}
            disabled={saving || isSuccess}
            className={`px-8 py-3 rounded-2xl font-black uppercase tracking-widest text-[11px] shadow-lg transition-all flex items-center justify-center gap-2
              ${isSuccess
                ? "bg-[#3ff2aa] text-[#0B2A36] scale-[1.01]"
                : "bg-[#3ff2aa] text-[#0B2A36] hover:scale-[1.01] active:scale-95 shadow-emerald-200/20"
              }
              disabled:opacity-50 disabled:cursor-not-allowed`}
            title="Guardar"
          >
            {isSuccess ? (
              <>
                <Check size={18} strokeWidth={4} className="animate-in zoom-in duration-300" />
                Guardado
              </>
            ) : saving ? (
              <>
                <RefreshCcw className="animate-spin" size={16} /> Guardando
              </>
            ) : (
              <>
                <Save size={16} /> Guardar Cambios
              </>
            )}
          </button>
        </div>

        {/* Footer spacing */}
        <div className="h-6" />
      </form>
    </div>
  );
};

export default EmpresaDatos;
